/**
 * Where to go once the group has picked something.
 *
 * The app answered "what should we watch" and then stopped, one step short of
 * being useful. This closes that step.
 *
 * Two deliberate limits:
 *
 * 1. **Search, never a title URL.** We do not know a provider's id for a film.
 *    The availability data in the catalog is placeholder data, and a
 *    constructed title URL would be a guess that 404s. A search link is
 *    honest: it lands on that provider with the title typed in.
 *
 * 2. **A search pattern only where it is known.** Several providers here have
 *    search URLs whose query parameter we are not sure of, and a wrong
 *    parameter is worse than none — it silently drops the title and dumps the
 *    viewer on an empty result page. Those get their front page instead,
 *    marked as such, so the UI can word the link truthfully.
 *
 * No API, no key, no request from us: these are URLs the browser opens.
 */
import { PROVIDER_INFO } from './catalog.js';
/**
 * Per provider: a function building a search URL, or a plain front page.
 *
 * German URLs where a provider has a German site, because the catalog only
 * describes DE availability today. When `COUNTRIES` grows beyond DE this needs
 * a country argument — until then a per-country table would be empty ceremony.
 */
const PATTERNS = {
    netflix: {
        search: (q) => `https://www.netflix.com/search?q=${q}`,
        home: 'https://www.netflix.com/',
    },
    'prime-video': {
        search: (q) => `https://www.primevideo.com/search/?phrase=${q}`,
        home: 'https://www.primevideo.com/',
    },
    'disney-plus': {
        search: (q) => `https://www.disneyplus.com/de-de/search?q=${q}`,
        home: 'https://www.disneyplus.com/de-de',
    },
    'apple-tv-plus': {
        search: (q) => `https://tv.apple.com/de/search?term=${q}`,
        home: 'https://tv.apple.com/de',
    },
    crunchyroll: {
        search: (q) => `https://www.crunchyroll.com/de/search?q=${q}`,
        home: 'https://www.crunchyroll.com/de',
    },
    mubi: {
        search: (q) => `https://mubi.com/de/de/search/films?query=${q}`,
        home: 'https://mubi.com/de/de',
    },
    'paramount-plus': {
        search: (q) => `https://www.paramountplus.com/de/search/?query=${q}`,
        home: 'https://www.paramountplus.com/de/',
    },
    // No search pattern we are confident about. Front page only, on purpose —
    // see the note at the top about wrong query parameters.
    wow: { home: 'https://www.wowtv.de/' },
    'rtl-plus': { home: 'https://plus.rtl.de/' },
    joyn: { home: 'https://www.joyn.de/' },
};
/**
 * A link to one provider for one title.
 *
 * The year is deliberately left out of the query. Providers' search boxes
 * match on title text; "The Thing 1982" reliably finds nothing.
 */
export function providerLink(provider, title) {
    const pattern = PATTERNS[provider];
    const encoded = encodeURIComponent(title.trim());
    const canSearch = pattern.search !== undefined && encoded.length > 0;
    return {
        provider,
        name: PROVIDER_INFO[provider].name,
        url: canSearch ? pattern.search(encoded) : pattern.home,
        kind: canSearch ? 'search' : 'home',
    };
}
/** Links for every provider a title is on, in the order the caller gave them. */
export function providerLinks(providers, title) {
    return providers.map((provider) => providerLink(provider, title));
}
